<template>
  <DataTable :value="tableData" :paginator="false">
    <!-- Expand rec table -->
    <template v-if="recommendationType === RecommendationType.EXPAND">
      <Column
        field="item_category"
        :header="translate(`${LOCALE_PREFIX}.table.columns.category`)"
        :sortable="true"
        v-bind="ColumnStyleClass.textColumn"
      />
      <Column
        field="item_name"
        :header="translate(`${LOCALE_PREFIX}.table.columns.item`)"
        :sortable="true"
        v-bind="ColumnStyleClass.textColumn"
      />
      <Column
        field="stations_offering_item"
        :header="translate(`${LOCALE_PREFIX}.table.columns.stationsOfferingItem`)"
        :sortable="true"
        v-bind="ColumnStyleClass.textColumn"
      />
      <Column
        field="daily_revenue"
        :header="translate(`${LOCALE_PREFIX}.table.columns.dailySales`)"
        :sortable="true"
        v-bind="ColumnStyleClass.numberColumn"
      >
        <template #body="{ data }">
          <span>{{ formatCurrency(data.daily_revenue) }}</span>
        </template>
      </Column>
      <Column
        field="daily_qty_sold"
        :header="translate(`${LOCALE_PREFIX}.table.columns.dailyQtySold`)"
        :sortable="true"
        v-bind="ColumnStyleClass.numberColumn"
      >
        <template #body="{ data }">
          <span>{{ data.daily_qty_sold }}</span>
        </template>
      </Column>
      <Column
        field="site_avg_daily_revenue"
        :header="translate(`${LOCALE_PREFIX}.table.columns.siteAvgDailySales`)"
        :sortable="true"
        v-bind="ColumnStyleClass.numberColumn"
      >
        <template #body="{ data }">
          <span>{{ formatCurrency(data.site_avg_daily_revenue) }}</span>
        </template>
      </Column>
      <Column
        field="site_avg_daily_qty_sold"
        :header="translate(`${LOCALE_PREFIX}.table.columns.siteAvgQty`)"
        :sortable="true"
        v-bind="ColumnStyleClass.numberColumn"
      >
        <template #body="{ data }">
          <span>{{ Math.round(data.site_avg_daily_qty_sold) }}</span>
        </template>
      </Column>
      <Column
        field="potential_additional_qty"
        :header="translate(`${LOCALE_PREFIX}.table.columns.potentialAdditionalQty`)"
        :sortable="true"
        v-bind="ColumnStyleClass.numberColumn"
      >
        <template #body="{ data }">
          <span>{{ data.potential_additional_qty }}</span>
        </template>
      </Column>
      <Column
        field="potential_additional_sales"
        :sortable="true"
        v-bind="ColumnStyleClass.numberColumn"
      >
        <template #header>
          <div class="flex align-items-center">
            <span>{{ translate(`${LOCALE_PREFIX}.table.columns.potentialAdditionalSales`) }}</span>
            <i
              class="material-symbols-rounded text-sm text-primary ml-2 cursor-pointer"
              v-tooltip.bottom="
                translate(`${LOCALE_PREFIX}.table.columns.potentialAdditionalSalesTooltip`)
              "
            >
              info
            </i>
          </div>
        </template>
        <template #body="{ data }">
          <span>{{ formatCurrency(data.potential_additional_sales) }}</span>
        </template>
      </Column>
    </template>

    <!-- Reduce rec table -->
    <template v-if="recommendationType === RecommendationType.REDUCE">
      <Column
        field="item_category"
        :header="translate(`${LOCALE_PREFIX}.table.columns.category`)"
        :sortable="true"
        v-bind="ColumnStyleClass.textColumn"
      />
      <Column
        field="item_name"
        :header="translate(`${LOCALE_PREFIX}.table.columns.item`)"
        :sortable="true"
        v-bind="ColumnStyleClass.textColumn"
      />
      <Column
        field="stations_offering_item"
        :header="translate(`${LOCALE_PREFIX}.table.columns.stationsOfferingItem`)"
        :sortable="true"
        v-bind="ColumnStyleClass.textColumn"
      />
      <Column
        field="daily_revenue"
        :header="translate(`${LOCALE_PREFIX}.table.columns.dailySales`)"
        :sortable="true"
        v-bind="ColumnStyleClass.numberColumn"
      >
        <template #body="{ data }">
          <span>{{ formatCurrency(data.daily_revenue) }}</span>
        </template>
      </Column>
      <Column
        field="daily_qty_sold"
        :header="translate(`${LOCALE_PREFIX}.table.columns.dailyQtySold`)"
        :sortable="true"
        v-bind="ColumnStyleClass.numberColumn"
      >
        <template #body="{ data }">
          <span>{{ data.daily_qty_sold }}</span>
        </template>
      </Column>
      <Column
        field="site_avg_daily_revenue"
        :header="translate(`${LOCALE_PREFIX}.table.columns.siteAvgDailySales`)"
        :sortable="true"
        v-bind="ColumnStyleClass.numberColumn"
      >
        <template #body="{ data }">
          <span>{{ formatCurrency(data.site_avg_daily_revenue) }}</span>
        </template>
      </Column>
      <Column
        field="site_avg_daily_qty_sold"
        :header="translate(`${LOCALE_PREFIX}.table.columns.siteAvgQty`)"
        :sortable="true"
        v-bind="ColumnStyleClass.numberColumn"
      >
        <template #body="{ data }">
          <span>{{ Math.round(data.site_avg_daily_qty_sold) }}</span>
        </template>
      </Column>
      <Column field="revenue_conservation" :sortable="true" v-bind="ColumnStyleClass.numberColumn">
        <template #header>
          <div class="flex align-items-center">
            <span>{{ translate(`${LOCALE_PREFIX}.table.columns.revenueConservation`) }}</span>
            <i
              class="material-symbols-rounded text-sm text-primary ml-2 cursor-pointer"
              v-tooltip.bottom="
                translate(`${LOCALE_PREFIX}.table.columns.revenueConservationTooltip`)
              "
            >
              info
            </i>
          </div>
        </template>
        <template #body="{ data }">
          <span>{{ formatCurrency(data.revenue_conservation) }}</span>
        </template>
      </Column>
    </template>
  </DataTable>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { DataTable, Column } from '@centric-os/components/src/core/data/data-table';
import { ColumnStyleClass } from '@centric-os/components/src/core/data/helpers/classes';
import { useTranslation, useCurrencyFormatter } from '@centric-os/helpers';
import { useRecommendationsStore } from '../../../../stores/use-recommendations-store';
import { SitesLocalePrefix } from '../../../../../enums';
import {
  type ExpandTableItemV2,
  type ReduceTableItemV2,
  RecommendationType,
} from '@centric-os/types/api/recommendations';

// translation
const { translate } = useTranslation();
const LOCALE_PREFIX = SitesLocalePrefix.SITE_OVERVIEW;

// composables
const { formatCurrency } = useCurrencyFormatter();

// store
const recommendationsStore = useRecommendationsStore();
const { currentRecommendation, currentRecommendationType } = storeToRefs(recommendationsStore);

// computeds
const tableData = computed<ExpandTableItemV2[] | ReduceTableItemV2[] | undefined>(() => {
  return currentRecommendation.value?.table;
});

const recommendationType = computed<RecommendationType | null>(() => {
  return currentRecommendationType.value;
});
</script>

<style scoped lang="scss">
:deep(.p-datatable) {
  .p-datatable-thead > tr > th {
    background-color: $neutralLightD1Color;
  }
}
</style>
